import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { Save, Download, AlertTriangle, Target, ChevronDown, ChevronUp, HelpCircle, RotateCcw, Sparkles, Check, X, Loader2, Bot } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import { useAutoSave } from '../../hooks/useAutoSave';
import { SavingIndicator } from '../../components/ui/SavingIndicator';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Question Definitions ─────────────────────────────────────────
const QUESTIONS = [
  { id: 'q1', section: 'prospect', text: 'Do we know (and can we influence) the key decision makers?' },
  { id: 'q2', section: 'prospect', text: "Is this opportunity aligned with the prospect's business strategy?" },
  { id: 'q3', section: 'prospect', text: 'Has the budget been formally approved and funded?' },
  { id: 'q4', section: 'prospect', text: 'Do we understand the business need or pain driving this bid?' },
  { id: 'q5', section: 'prospect', text: 'Are requirements clearly defined, and are they technically feasible?' },
  { id: 'q6', section: 'prospect', text: 'Do we know the evaluation criteria and how it will be weighted?' },
  { id: 'q7', section: 'prospect', text: 'Are there serious business/technical/financial issues behind the scene?' },
  { id: 'q8', section: 'prospect', text: 'Do we know the proposal time frame, and is it realistic?' },
  { id: 'q9', section: 'prospect', text: "Has the prospect already made a 'buy or build' decision?" },
  { id: 'q10', section: 'prospect', text: 'Is low price a major factor in selecting the winning bid?' },
  { id: 'q11', section: 'prospect', text: 'Are there penalties for not delivering on time or within budget?' },
  { id: 'q12', section: 'prospect', text: 'Are the contract terms and conditions acceptable to us?' },
  { id: 'q13', section: 'internal', text: 'Is this opportunity in sync with our own strategic direction?' },
  { id: 'q14', section: 'internal', text: 'Do we have strong management support and sponsorship?' },
  { id: 'q15', section: 'internal', text: 'Do we have the resources, talent, and will to win this opportunity?' },
  { id: 'q16', section: 'internal', text: 'Do we have the resources and ability to deliver, if we win?' },
  { id: 'q17', section: 'internal', text: 'Can we realistically manage the risks, if we win?' },
  { id: 'q18', section: 'internal', text: 'If additional resources are needed, can we get what we need?' },
  { id: 'q19', section: 'internal', text: 'How well are we known within this business sector?' },
  { id: 'q20', section: 'internal', text: 'Do we have a successful track record with similar opportunities?' },
  { id: 'q21', section: 'internal', text: 'Do we have a relationship with this prospect, and is it favorable?' },
  { id: 'q22', section: 'internal', text: 'Are partners needed, and if so, will they complicate our situation?' },
  { id: 'q23', section: 'internal', text: 'Do we have differentiators that improve our odds of winning?' },
  { id: 'q24', section: 'internal', text: 'Can we afford the investment needed to pursue this opportunity?' },
  { id: 'q25', section: 'internal', text: 'Will winning put any of our existing business at risk?' },
  { id: 'q26', section: 'internal', text: 'Can we contractually protect our intellectual property?' },
  { id: 'q27', section: 'internal', text: 'Are there consequences to us of losing or not bidding?' },
  { id: 'q28', section: 'market', text: 'Do we know who the other competitive bidders are, if any?' },
  { id: 'q29', section: 'market', text: 'Is a competitor an incumbent, and does that pose a threat?' },
  { id: 'q30', section: 'market', text: 'Is a competitor favored by prospect decision makers or influencers?' },
  { id: 'q31', section: 'market', text: 'Are we at a distinct competitive disadvantage from the start?' },
  { id: 'q32', section: 'market', text: 'Does this solution involve new or unproven technologies?' },
  { id: 'q33', section: 'market', text: 'Will winning enhance our reputation and market positioning?' },
  { id: 'q34', section: 'market', text: 'Will winning open up new market opportunities for us?' },
  { id: 'q35', section: 'market', text: 'Will winning give us an advantage over our competitors?' },
];

const SECTIONS = [
  { key: 'prospect', label: 'Prospect Information', shortLabel: 'Prospect', focus: 'P(Go)', count: 12, maxScore: 60, color: 'blue' },
  { key: 'internal', label: 'Internal Information', shortLabel: 'Internal', focus: 'P(Win)', count: 15, maxScore: 75, color: 'violet' },
  { key: 'market', label: 'Market / Competitive Information', shortLabel: 'Market', focus: 'Strategy', count: 8, maxScore: 40, color: 'orange' },
];

const SCORE_OPTIONS = [
  { value: 5, label: '5', tip: 'Strong advantage' },
  { value: 4, label: '4', tip: 'Moderate advantage' },
  { value: 3, label: '3', tip: 'Neutral' },
  { value: 2, label: '2', tip: 'Moderate disadvantage' },
  { value: 1, label: '1', tip: 'Serious disadvantage' },
  { value: 0, label: 'Unk', tip: 'Unknown' },
];

function getScoreColor(val) {
  if (val === 5) return 'bg-emerald-600 text-white border-emerald-700';
  if (val === 4) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
  if (val === 3) return 'bg-slate-100 text-slate-700 border-slate-300';
  if (val === 2) return 'bg-amber-100 text-amber-800 border-amber-300';
  if (val === 1) return 'bg-red-100 text-red-800 border-red-300';
  return 'bg-slate-50 text-slate-500 border-slate-200';
}

function computeTotals(responses) {
  const result = {};
  let grandScore = 0, grandMax = 0, grandDK = 0, grandUnk = 0, grandAnswered = 0;

  for (const sec of SECTIONS) {
    const secQs = QUESTIONS.filter(q => q.section === sec.key);
    let score = 0, dk = 0, unk = 0, answered = 0;
    for (const q of secQs) {
      const r = responses[q.id];
      if (r) {
        if (r.score > 0) { score += r.score; answered++; }
        else unk++;
        if (r.deal_killer) dk++;
      }
    }
    result[sec.key] = { score, maxScore: sec.maxScore, dealKillers: dk, unknowns: unk, answered, pct: sec.maxScore > 0 ? Math.round((score / sec.maxScore) * 100) : 0 };
    grandScore += score; grandMax += sec.maxScore; grandDK += dk; grandUnk += unk; grandAnswered += answered;
  }
  result.total = { score: grandScore, maxScore: grandMax, dealKillers: grandDK, unknowns: grandUnk, answered: grandAnswered, pct: grandMax > 0 ? Math.round((grandScore / grandMax) * 100) : 0 };
  return result;
}

// ── Score Radio Button ─────────────────────────────────────────
function ScoreRadio({ questionId, value, selected, onChange, label, tip }) {
  const isSelected = selected === value;
  const isUnk = value === 0;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={`score-${questionId}-${value}`}
            onClick={() => onChange(questionId, value)}
            className={cn(
              'w-9 h-9 rounded-md text-xs font-bold transition-all duration-150 cursor-pointer',
              // Unk is visually distinct: dashed border to signal "not a numerical score"
              isUnk
                ? isSelected
                  ? 'border-2 border-dashed border-slate-500 bg-slate-200 text-slate-700'
                  : 'border-2 border-dashed border-slate-300 bg-white text-slate-400 hover:border-slate-400 hover:text-slate-500'
                : isSelected
                  ? cn('border', getScoreColor(value))
                  : 'border bg-white text-slate-400 border-slate-200 hover:border-slate-400 hover:bg-slate-50'
            )}
          >
            {label}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top"><p className="text-xs">{tip}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Deal Killer Toggle ─────────────────────────────────────────
function DealKillerToggle({ questionId, checked, onChange, disabled, disabledReason }) {
  const labelEl = (
    <label
      data-testid={`dk-${questionId}`}
      aria-disabled={disabled || undefined}
      className={cn(
        'flex items-center gap-1.5 px-2 py-1 rounded-md border transition-all text-xs',
        disabled
          ? 'bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed opacity-60'
          : checked
            ? 'bg-red-100 border-red-300 text-red-700 cursor-pointer'
            : 'bg-white border-slate-200 text-slate-400 hover:border-red-200 hover:text-red-400 cursor-pointer'
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(questionId, e.target.checked)}
        className="sr-only"
      />
      <AlertTriangle className={cn('w-3 h-3', checked && !disabled ? 'text-red-600' : 'text-slate-300')} />
      <span className="hidden sm:inline">Deal Killer</span>
      <span className="sm:hidden">DK</span>
    </label>
  );
  if (!disabled) return labelEl;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{labelEl}</TooltipTrigger>
        <TooltipContent side="left"><p className="text-xs max-w-[180px]">{disabledReason}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Main Scorecard Component ─────────────────────────────────────
export default function BidPursuitScorecard({ pursuitId, token }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [responses, setResponses] = useState({});
  const [notes, setNotes] = useState('');
  const [downloading, setDownloading] = useState(false);
  // Per-pursuit localStorage-backed UI state (collapsed sections + unanswered-only filter)
  const uiStateKey = pursuitId ? `scorecard-ui:${pursuitId}` : null;
  const loadUiState = () => {
    if (typeof window === 'undefined' || !uiStateKey) return {};
    try { return JSON.parse(window.localStorage.getItem(uiStateKey) || '{}'); } catch { return {}; }
  };
  const initialUi = loadUiState();
  const [isExpanded, setIsExpanded] = useState(initialUi.isExpanded ?? true);
  const [collapsedSections, setCollapsedSections] = useState(initialUi.collapsedSections || {});
  const [showUnansweredOnly, setShowUnansweredOnly] = useState(initialUi.showUnansweredOnly ?? false);
  const [showLegend, setShowLegend] = useState(initialUi.showLegend ?? false);
  const [lastSaved, setLastSaved] = useState(null);
  // AI Pre-fill state
  const [aiSuggestions, setAiSuggestions] = useState(null); // { q1: { score, confidence, rationale, deal_killer }, ... }
  const [aiLoading, setAiLoading] = useState(false);
  const [aiModelUsed, setAiModelUsed] = useState(null);
  // Guard so auto-save doesn't fire on the initial GET populate
  const hasLoadedRef = useRef(false);

  // Persist UI preferences per pursuit whenever they change
  useEffect(() => {
    if (typeof window === 'undefined' || !uiStateKey) return;
    try {
      window.localStorage.setItem(uiStateKey, JSON.stringify({
        isExpanded,
        collapsedSections,
        showUnansweredOnly,
        showLegend,
      }));
    } catch { /* quota / private mode — ignore */ }
  }, [uiStateKey, isExpanded, collapsedSections, showUnansweredOnly, showLegend]);

  const totals = computeTotals(responses);

  // Auto-save backend call (silent — SavingIndicator surfaces state).
  // On successful save, also notify the pWin Signal Tracker so its composite can recompute.
  const persistScorecard = useCallback(async ({ responses: r, notes: n }) => {
    const res = await fetch(`${API}/api/pursuits/${pursuitId}/scorecard`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ responses: r, notes: n }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const result = await res.json().catch(() => ({}));
    // Best-effort signal broadcast — pWin Signal Tracker will re-fetch when it hears this
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pwin-signal-updated', {
        detail: { pursuitId, source: 'scorecard' },
      }));
    }
    return result;
  }, [pursuitId, token]);

  const { save: autoSave, saveNow, isSaving: autoSaving, lastSaved: autoLastSaved, error: autoSaveError } = useAutoSave(persistScorecard, 2000);

  // Load scorecard
  useEffect(() => {
    if (!pursuitId || !token) return;
    fetch(`${API}/api/pursuits/${pursuitId}/scorecard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (data.scorecard?.responses) {
          setResponses(data.scorecard.responses);
          setNotes(data.scorecard.notes || '');
          setLastSaved(data.scorecard.updated_at);
        }
      })
      .catch(() => {})
      .finally(() => {
        // Tiny delay ensures state flush above doesn't immediately trigger autoSave
        setTimeout(() => { hasLoadedRef.current = true; }, 0);
      });
  }, [pursuitId, token]);

  // Auto-save whenever responses or notes change (after initial load)
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (!pursuitId || !token) return;
    autoSave({ responses, notes });
  }, [responses, notes, autoSave, pursuitId, token]);

  const handleScoreChange = useCallback((qId, value) => {
    setResponses(prev => ({
      ...prev,
      // When user marks a score as Unknown (0), auto-clear any Deal Killer flag
      // to avoid the semantic conflict "I don't know, but it's also a dealbreaker".
      [qId]: {
        ...prev[qId],
        score: value,
        deal_killer: value === 0 ? false : (prev[qId]?.deal_killer || false)
      }
    }));
  }, []);

  const handleDealKillerChange = useCallback((qId, checked) => {
    setResponses(prev => ({
      ...prev,
      [qId]: { ...prev[qId], score: prev[qId]?.score ?? null, deal_killer: checked }
    }));
  }, []);

  const handleSave = async () => {
    // Force-flush any pending auto-save (used implicitly via saveNow elsewhere,
    // kept here for API compatibility if anything external triggers a save)
    try {
      await saveNow({ responses, notes });
      toast.success('Scorecard saved');
    } catch {
      toast.error('Failed to save scorecard');
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // Flush any pending auto-save so the PDF reflects latest data
      try {
        await saveNow({ responses, notes });
      } catch {
        toast.error('Could not save scorecard before export — PDF aborted');
        setDownloading(false);
        return;
      }
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/scorecard/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Bid_Pursuit_Scorecard.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      } else {
        toast.error('Failed to generate PDF');
      }
    } catch {
      toast.error('Network error');
    }
    setDownloading(false);
  };

  const handleReset = () => {
    if (window.confirm('Clear all 35 responses plus notes? This cannot be undone.')) {
      setResponses({});
      setNotes('');
      setAiSuggestions(null);
      setAiModelUsed(null);
      toast.info('Scorecard cleared');
    }
  };

  // AI Pre-fill handlers
  const handleAiPrefill = async () => {
    setAiLoading(true);
    // 120s hard timeout — LLM calls can take 20-60s for large KBs.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/scorecard/ai-prefill`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (res.ok) {
        const data = await res.json();
        setAiSuggestions(data.suggestions);
        setAiModelUsed(data.model_used);
        const scored = data.questions_scored ?? Object.keys(data.suggestions || {}).length;
        const total = data.questions_total ?? 35;
        if (data.partial && Array.isArray(data.missing_sections) && data.missing_sections.length) {
          toast.warning(
            `AI scored ${scored}/${total} — ${data.missing_sections.join(', ')} section${data.missing_sections.length > 1 ? 's' : ''} failed. You can retry or score manually.`,
            { duration: 7000 }
          );
        } else {
          toast.success(`AI scored ${scored}/${total} questions using ${data.model_used}`);
        }
      } else {
        const raw = await res.text();
        let detail = '';
        let detailObj = null;
        try {
          const parsed = JSON.parse(raw);
          detail = parsed?.detail || '';
          if (typeof parsed?.detail === 'object') detailObj = parsed.detail;
        } catch { /* non-JSON body */ }
        // 400 with code=BYOM_NOT_CONFIGURED → prompt the org to set up their
        // own LLM key instead of a confusing generic failure.
        if (res.status === 400 && detailObj?.code === 'BYOM_NOT_CONFIGURED') {
          toast.error(
            'BYOM key required',
            {
              description: 'This organization has no BYOM (bring-your-own-model) LLM API key configured. Add one in Settings → AI Config to use AI features.',
              duration: 9000,
              action: {
                label: 'Configure BYOM',
                onClick: () => navigate(
                  user?.workspaceId ? `/${user.workspaceId}/settings` : '/admin/dashboard'
                ),
              },
            }
          );
        } else {
          const msg = (typeof detail === 'string' && detail) || `AI analysis failed (HTTP ${res.status})`;
          console.error('[scorecard ai-prefill] server error', res.status, raw.slice(0, 500));
          toast.error(msg);
        }
      }
    } catch (err) {
      console.error('[scorecard ai-prefill] fetch failed', err);
      if (err?.name === 'AbortError') {
        toast.error('AI analysis timed out after 2 minutes. Try again — backend may be warming up.');
      } else {
        toast.error(`Network error during AI analysis: ${err?.message || 'unknown cause'}`);
      }
    } finally {
      clearTimeout(timeoutId);
      setAiLoading(false);
    }
  };

  const handleAcceptAllAi = () => {
    if (!aiSuggestions) return;
    // Safety guard: count how many existing answered questions would be overwritten
    const wouldOverwrite = Object.keys(aiSuggestions).filter(qId => {
      const r = responses[qId];
      return r && (r.score !== null && r.score !== undefined);
    });
    if (wouldOverwrite.length > 0) {
      const confirmed = window.confirm(
        `This will overwrite ${wouldOverwrite.length} existing response${wouldOverwrite.length === 1 ? '' : 's'}. Continue?\n\nTip: use the per-question "Accept" button to apply AI only to un-answered questions.`
      );
      if (!confirmed) return;
    }
    const merged = { ...responses };
    for (const [qId, suggestion] of Object.entries(aiSuggestions)) {
      merged[qId] = { score: suggestion.score, deal_killer: suggestion.deal_killer };
    }
    setResponses(merged);
    toast.success('All AI suggestions applied');
  };

  const handleAcceptAiQuestion = (qId) => {
    const suggestion = aiSuggestions?.[qId];
    if (!suggestion) return;
    setResponses(prev => ({
      ...prev,
      [qId]: { score: suggestion.score, deal_killer: suggestion.deal_killer }
    }));
  };

  const handleDismissAi = () => {
    setAiSuggestions(null);
    setAiModelUsed(null);
    toast.info('AI suggestions dismissed');
  };

  const toggleSection = (key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const answeredCount = Object.values(responses).filter(r => r.score !== null && r.score !== undefined).length;

  return (
    <Card className="border-slate-300 shadow-md" data-testid="bid-pursuit-scorecard">
      {/* Card Header */}
      <CardHeader
        className="cursor-pointer border-b border-slate-200 bg-slate-900 rounded-t-lg"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-amber-400" />
            <CardTitle className="text-white text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bid Pursuit Scorecard
            </CardTitle>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    className="text-slate-400 hover:text-white transition-colors"
                    data-testid="scorecard-help-icon"
                    aria-label="About the Bid Pursuit Scorecard"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[300px]">
                  <div className="text-xs space-y-1.5">
                    <p className="font-semibold">FEDSIM-style Bid Pursuit Scorecard</p>
                    <p>35 questions across 3 sections: <strong>Prospect</strong> drives P(Go), <strong>Internal</strong> drives P(Win), <strong>Market</strong> drives Strategy.</p>
                    <p>Score 1–5 (or <em>Unk</em> if unknown). Flag any question as a <strong>Deal Killer</strong> to force a No-Go regardless of score.</p>
                    <p>Max score = 35 questions × 5 points = <strong>175</strong>.</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs">
              {answeredCount}/35
            </Badge>
            {totals.total.dealKillers > 0 && !isExpanded && (
              <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs" data-testid="scorecard-collapsed-dk-badge">
                <AlertTriangle className="w-3 h-3 mr-1" />{totals.total.dealKillers} Deal Killer{totals.total.dealKillers > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            {answeredCount > 0 && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="text-lg font-bold text-white cursor-help"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {totals.total.score} / {totals.total.maxScore}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="text-xs">Max = 35 questions × 5 points = {totals.total.maxScore}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-0">
          {/* AI Pre-fill Bar */}
          {!aiSuggestions && (
            <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-50 to-violet-50 border-b border-indigo-200" data-testid="ai-prefill-bar">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-500" />
                <span className="text-sm font-medium text-indigo-800">AI-Assisted Scoring</span>
                <span className="text-xs text-indigo-500">Analyze pursuit data to pre-fill scores</span>
              </div>
              <Button
                data-testid="ai-prefill-btn"
                size="sm"
                disabled={aiLoading}
                onClick={(e) => { e.stopPropagation(); handleAiPrefill(); }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1.5"
              >
                {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {aiLoading ? 'Analyzing...' : 'Pre-fill with AI'}
              </Button>
            </div>
          )}

          {/* AI Suggestions Active Banner */}
          {aiSuggestions && (
            <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 to-violet-100 border-b border-indigo-300" data-testid="ai-suggestions-banner">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-800">AI Suggestions Ready</span>
                {aiModelUsed && <Badge className="bg-indigo-200 text-indigo-700 text-[10px]">{aiModelUsed}</Badge>}
                <span className="text-xs text-indigo-600">Review and accept suggestions below</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="ai-accept-all-btn"
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); handleAcceptAllAi(); }}
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 text-xs gap-1"
                >
                  <Check className="w-3 h-3" /> Apply All Suggestions
                </Button>
                <Button
                  data-testid="ai-dismiss-btn"
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); handleDismissAi(); }}
                  className="text-slate-500 hover:text-red-500 text-xs gap-1"
                >
                  <X className="w-3 h-3" /> Dismiss
                </Button>
              </div>
            </div>
          )}

          {/* Legend — collapsible; filter toggle stays visible even when collapsed */}
          <div className="border-b border-slate-200 bg-slate-50 text-xs text-slate-600" data-testid="scorecard-legend">
            <div className="flex items-center gap-3 px-5 py-2">
              <button
                type="button"
                onClick={() => setShowLegend(v => !v)}
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 font-semibold"
                data-testid="scorecard-legend-toggle"
                aria-expanded={showLegend}
              >
                {showLegend ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                <span>Scoring legend</span>
              </button>

              {!showLegend && (
                <span className="text-slate-400 italic hidden md:inline">
                  5 = Strong · 1 = Serious disadvantage · Unk = Unknown · ⚠ Deal Killer
                </span>
              )}

              {/* Unanswered-only filter — always visible */}
              <label
                className={cn(
                  "ml-auto flex items-center gap-1.5 px-2 py-1 rounded-md border cursor-pointer transition-colors",
                  showUnansweredOnly
                    ? "bg-indigo-100 border-indigo-300 text-indigo-700"
                    : "bg-white border-slate-200 text-slate-500 hover:border-indigo-200"
                )}
                data-testid="scorecard-unanswered-filter"
              >
                <input
                  type="checkbox"
                  checked={showUnansweredOnly}
                  onChange={(e) => setShowUnansweredOnly(e.target.checked)}
                  className="sr-only"
                />
                <span className={cn("w-3.5 h-3.5 rounded border flex items-center justify-center", showUnansweredOnly ? "bg-indigo-600 border-indigo-700" : "border-slate-300")}>
                  {showUnansweredOnly && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                <span className="font-medium text-xs">Show unanswered only ({35 - answeredCount})</span>
              </label>
            </div>

            {showLegend && (
              <div className="flex flex-wrap items-center gap-3 px-5 pb-3 pt-0">
                {SCORE_OPTIONS.map(opt => (
                  <span key={opt.value} className="flex items-center gap-1">
                    <span className={cn('w-5 h-5 rounded text-center text-[10px] font-bold leading-5 border', getScoreColor(opt.value))}>{opt.label}</span>
                    <span className="text-slate-500">{opt.tip}</span>
                  </span>
                ))}
                <span className="flex items-center gap-1 ml-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                  <span className="text-red-600 font-medium">Deal Killer</span>
                </span>
              </div>
            )}
          </div>

          {/* Sections */}
          {SECTIONS.map(sec => {
            const secQs = QUESTIONS.filter(q => q.section === sec.key);
            const isCollapsed = collapsedSections[sec.key];
            const sectionColors = {
              blue: 'border-l-blue-500 bg-blue-50',
              violet: 'border-l-violet-500 bg-violet-50',
              orange: 'border-l-orange-500 bg-orange-50',
            };
            const t = totals[sec.key] || {};
            const pct = t.pct || 0;

            return (
              <div key={sec.key} className="border-b border-slate-200 last:border-0" data-testid={`scorecard-section-${sec.key}`}>
                {/* Section Header — now carries all progress signals */}
                <button
                  type="button"
                  onClick={() => toggleSection(sec.key)}
                  className={cn('w-full flex items-center justify-between px-5 py-3 border-l-4 transition-colors', sectionColors[sec.color] || 'bg-slate-50')}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="text-sm font-bold text-slate-800 whitespace-nowrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {sec.shortLabel}
                      <span className="font-normal text-slate-500 text-xs ml-1.5">· {sec.focus}</span>
                    </h3>
                    <Badge variant="outline" className="text-xs">{sec.count} questions</Badge>
                    {(t.dealKillers || 0) > 0 && (
                      <Badge className="bg-red-100 text-red-700 text-xs" data-testid={`section-${sec.key}-dk-badge`}>
                        <AlertTriangle className="w-3 h-3 mr-1" />{t.dealKillers} DK
                      </Badge>
                    )}
                    {(t.unknowns || 0) > 0 && (
                      <Badge className="bg-slate-100 text-slate-600 text-xs">
                        <HelpCircle className="w-3 h-3 mr-1" />{t.unknowns} Unk
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {/* Inline mini progress bar — fast at-a-glance scan */}
                    <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden hidden sm:block" aria-label={`${sec.label} progress ${pct}%`}>
                      <div className={cn('h-full rounded-full transition-all duration-500', pct >= 70 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : pct > 0 ? 'bg-red-500' : 'bg-slate-300')} style={{ width: `${pct}%` }} />
                    </div>
                    <Badge className={cn('text-xs', pct >= 70 ? 'bg-emerald-100 text-emerald-700' : pct >= 50 ? 'bg-amber-100 text-amber-700' : pct > 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500')}>
                      {pct}%
                    </Badge>
                    <span className="text-sm font-bold text-slate-700 whitespace-nowrap">{(t.score || 0)} / {sec.maxScore}</span>
                    {isCollapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {/* Questions */}
                {!isCollapsed && (() => {
                  const visibleQs = showUnansweredOnly
                    ? secQs.filter(q => {
                        const r = responses[q.id];
                        return !r || r.score === null || r.score === undefined;
                      })
                    : secQs;

                  if (visibleQs.length === 0) {
                    return (
                      <div className="px-5 py-6 text-center text-sm text-slate-500 bg-slate-50/50">
                        All {secQs.length} questions in this section are answered.
                      </div>
                    );
                  }

                  return (
                    <div className="divide-y divide-slate-100">
                      {visibleQs.map((q, idx) => {
                        const r = responses[q.id] || {};
                        const qNum = q.id.replace('q', '');
                        const isDK = r.deal_killer || false;
                        const aiSugg = aiSuggestions?.[q.id];
                        const hasAiSuggestion = aiSugg && (r.score === null || r.score === undefined);
                        return (
                          <div
                            key={q.id}
                            data-testid={`question-${q.id}`}
                            className={cn(
                              'flex items-center gap-3 px-5 py-3 transition-colors',
                              isDK ? 'bg-red-50' : hasAiSuggestion ? 'bg-indigo-50/40' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                            )}
                          >
                            {/* Question Number */}
                            <span className="text-xs font-bold text-slate-400 w-6 text-right shrink-0">{qNum}.</span>

                            {/* Question Text + AI Rationale */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-700">{q.text}</p>
                              {aiSugg && (
                                <div className="flex items-start gap-2 mt-1.5 flex-wrap" data-testid={`ai-rationale-${q.id}`}>
                                  <Bot className="w-3.5 h-3.5 text-indigo-400 shrink-0 mt-0.5" />
                                  <span className="text-xs text-indigo-700 leading-snug flex-1 min-w-0">{aiSugg.rationale}</span>
                                  <Badge className={cn('text-[10px] px-1.5 py-0 shrink-0 uppercase tracking-wide',
                                    aiSugg.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                                    aiSugg.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                                    'bg-slate-100 text-slate-500'
                                  )}>{aiSugg.confidence}</Badge>
                                  {hasAiSuggestion && (
                                    <button
                                      data-testid={`ai-accept-${q.id}`}
                                      onClick={() => handleAcceptAiQuestion(q.id)}
                                      title={`Apply score ${aiSugg.score === 0 ? 'Unknown' : aiSugg.score}${aiSugg.deal_killer ? ' and mark as Deal Killer' : ''}`}
                                      className="ml-auto px-2 py-0.5 text-[11px] font-semibold bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors shrink-0 flex items-center gap-1"
                                    >
                                      <Check className="w-3 h-3" />
                                      Apply score {aiSugg.score === 0 ? 'Unknown' : aiSugg.score}
                                      {aiSugg.deal_killer && <AlertTriangle className="w-3 h-3 text-red-500" />}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Score Buttons */}
                            <div className="flex items-center gap-1.5 shrink-0">
                              {SCORE_OPTIONS.map(opt => (
                                <ScoreRadio
                                  key={opt.value}
                                  questionId={q.id}
                                  value={opt.value}
                                  selected={r.score}
                                  onChange={handleScoreChange}
                                  label={opt.label}
                                  tip={opt.tip}
                                />
                              ))}
                            </div>

                            {/* Deal Killer — disabled when score is 0 (Unknown) to avoid semantic conflict */}
                            <DealKillerToggle
                              questionId={q.id}
                              checked={isDK}
                              onChange={handleDealKillerChange}
                              disabled={r.score === 0}
                              disabledReason="Deal Killer is disabled while score is Unknown. Score the question first."
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            );
          })}

          {/* Grand Total */}
          <div className="px-5 py-4 bg-slate-900" data-testid="scorecard-grand-total">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Total Score</p>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-2xl font-bold text-white cursor-help" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {totals.total.score} <span className="text-base text-slate-400">/ {totals.total.maxScore}</span>
                        </p>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-xs">Max = 35 questions × 5 points = {totals.total.maxScore}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Badge className={cn(
                  'text-sm px-3 py-1',
                  totals.total.pct >= 70 ? 'bg-emerald-500/20 text-emerald-300' :
                  totals.total.pct >= 50 ? 'bg-amber-500/20 text-amber-300' :
                  'bg-red-500/20 text-red-300'
                )}>
                  {totals.total.pct}%
                </Badge>
              </div>
              <div className="flex items-center gap-3">
                {totals.total.dealKillers > 0 && (
                  <Badge className="bg-red-500/20 text-red-300 border-red-500/30">
                    <AlertTriangle className="w-3.5 h-3.5 mr-1" />{totals.total.dealKillers} Deal Killer{totals.total.dealKillers > 1 ? 's' : ''}
                  </Badge>
                )}
                {totals.total.unknowns > 0 && (
                  <Badge className="bg-slate-500/20 text-slate-300 border-slate-500/30">
                    <HelpCircle className="w-3.5 h-3.5 mr-1" />{totals.total.unknowns} Unknown{totals.total.unknowns > 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
            </div>
            {/* Progress bars per section — unified labels with section headers */}
            <div className="grid grid-cols-3 gap-3 mt-3">
              {SECTIONS.map(sec => {
                const t = totals[sec.key] || {};
                const pct = t.pct || 0;
                return (
                  <div key={sec.key}>
                    <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                      <span>
                        <span className="text-slate-300 font-medium">{sec.shortLabel}</span>
                        <span className="text-slate-500 ml-1">· {sec.focus}</span>
                      </span>
                      <span>{pct}%</span>
                    </div>
                    <div
                      className="h-1.5 bg-slate-700 rounded-full overflow-hidden"
                      role="progressbar"
                      aria-valuenow={pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${sec.shortLabel} (${sec.focus}) progress ${pct}%`}
                    >
                      <div className={cn('h-full rounded-full transition-all duration-500', pct >= 70 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : pct > 0 ? 'bg-red-500' : 'bg-slate-600')} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes & Actions */}
          <div className="px-5 py-4 bg-white border-t border-slate-200">
            <Textarea
              data-testid="scorecard-notes"
              placeholder="Notes, observations, or rationale for the scoring decisions..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mb-3 text-sm min-h-[60px]"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button data-testid="scorecard-pdf-btn" onClick={handleDownloadPDF} disabled={downloading} variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-1" />{downloading ? 'Generating…' : 'Download PDF'}
                </Button>
                <Button data-testid="scorecard-reset-btn" onClick={handleReset} variant="ghost" size="sm" className="text-slate-400 hover:text-red-500">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />Clear All
                </Button>
              </div>
              <SavingIndicator
                isSaving={autoSaving}
                lastSaved={autoLastSaved || (lastSaved ? new Date(lastSaved) : null)}
                error={autoSaveError}
              />
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
